import { Lock } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { formatDate, formatMoney } from '@/lib/utils'

import { SUSPENDED_EVENT, type PaymentNotice } from './types'

/**
 * Škola pozastavená pre neuhradenú faktúru. Server namiesto dát vráti 402
 * s údajmi na úhradu (`TenantMiddleware`); klient API ich pošle udalosťou
 * a tu sa ukáže obrazovka cez celú aplikáciu - bez nej by škola videla len
 * nefunkčné prihlásenie.
 */
export default function SuspendedScreen() {
  const { t } = useTranslation()
  const [notice, setNotice] = useState<PaymentNotice | null>(null)

  useEffect(() => {
    const onSuspended = (event: Event) => setNotice((event as CustomEvent<PaymentNotice>).detail)
    window.addEventListener(SUSPENDED_EVENT, onSuspended)
    return () => window.removeEventListener(SUSPENDED_EVENT, onSuspended)
  }, [])

  if (!notice) return null

  const rows: Array<[string, string]> = [
    [t('billing.invoice'), notice.number],
    [t('billing.amount'), formatMoney(Number(notice.total))],
    [t('billing.variableSymbol'), notice.variable_symbol],
    [t('billing.iban'), notice.iban],
    [t('billing.beneficiary'), notice.beneficiary],
  ]

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto bg-slate-100 px-4 py-10">
      <div className="mx-auto max-w-lg rounded-lg border border-slate-200 bg-white p-6 shadow-lg">
        <div className="flex items-center gap-2 text-red-700">
          <Lock size={20} />
          <h1 className="text-lg font-semibold">{t('billing.suspendedTitle')}</h1>
        </div>
        <p className="mt-3 text-sm text-slate-700">
          {t('billing.suspendedText', { number: notice.number, due: formatDate(notice.due) })}
        </p>
        <div className="mt-5 flex flex-col items-center gap-5 sm:flex-row sm:items-start">
          {notice.qr ? (
            <div className="flex shrink-0 flex-col items-center gap-1.5">
              <img src={notice.qr} alt="QR" className="h-40 w-40 rounded border border-slate-200" />
              <img src="/images/pay-by-square.svg" alt="PAY by square" className="h-7" />
            </div>
          ) : null}
          <dl className="grid w-full grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
            {rows.map(([label, value]) => (
              <div key={label} className="contents">
                <dt className="text-slate-500">{label}</dt>
                <dd className="break-all font-medium">{value}</dd>
              </div>
            ))}
          </dl>
        </div>
        <p className="mt-5 text-xs text-slate-500">{t('billing.suspendedAfterPay')}</p>
      </div>
    </div>
  )
}
